/**
 * Error object for each spreadsheet row
 */
export type ErrorResponse = {
  index: number;
  errorColumnList: string[];
  messageList: ErrorMessage[];
};

export const INIT_ERROR_RESULT_DATA: ErrorSpreadsheetResult = {
  errorColumnList: [],
  errorMessage: "",
  messageList: [],
};

/**
 * Error message object for each column
 */
export type ErrorMessage = {
  errorColumn: string | null;
  messageId: string;
  message: string;
  backendError?: boolean;
};

/**
 * Object that corresponds to spreadsheetState to update spreadsheetState
 */
export type ErrorSpreadsheetResult = {
  index?: number;
  errorColumnList: string[];
  errorMessage: string;
  messageList: ErrorMessage[];
};

export type DisplayColumn = {
  label: string;
  value: string;
};

/**
 * Converts backend API error response to spreadsheetState type.
 * @param apiErrorResponse Array of ErrorResponse that will be returned in body of exception.
 * @param spreadsheetResult Current spreadsheetState as array of SpreadsheetResult.
 * @param displayColumnList List of the label and value set of spreadsheet.
 * @returns array of SpreadsheetResult to set to spreadsheetState.
 */
export const mappingErrorResponseToSpreadsheet = <
  T extends ErrorSpreadsheetResult,
>(
  apiErrorResponse: any,
  spreadsheetResult: T[],
  displayColumnList: DisplayColumn[],
): T[] => {
  if (!apiErrorResponse || !Array.isArray(apiErrorResponse)) {
    throw new Error("API error response is invalid.");
  }
  let updatedSpreadsheet: T[] = JSON.parse(JSON.stringify(spreadsheetResult));
  apiErrorResponse.forEach((error: ErrorResponse) => {
    const updateRowIndex = error.index;
    const errorMessageList = error.messageList.map((element) => {
      const displayColumn =
        displayColumnList.find((col) => col.value === element.errorColumn)
          ?.label ?? element.errorColumn;
      return formattedMessage(displayColumn, element);
    });
    let updatespreadsheetRow: T | undefined = spreadsheetResult[updateRowIndex];
    if (updatespreadsheetRow == null) {
      return updatedSpreadsheet;
    }
    updatespreadsheetRow.errorMessage = errorMessageList.join("\n");
    updatespreadsheetRow.errorColumnList = error.errorColumnList;
    updatespreadsheetRow.messageList = error.messageList;
    updatedSpreadsheet.splice(updateRowIndex, 1, updatespreadsheetRow);
  });
  return updatedSpreadsheet;
};

const formattedMessage = (
  displayColumn: string | null,
  element: { errorColumn?: string | null; messageId: any; message: any },
) => {
  if (!element.messageId) {
    return `[${displayColumn}] ${element.message}`;
  }
  if (!displayColumn) {
    return `[${element.messageId}] ${element.message}`;
  }
  return `[${displayColumn}: ${element.messageId}] ${element.message}`;
};

/**
 * Clears Frontend errors from messageList and errorColumnList.
 * @param rowItem One line that was edited
 */
export const clearFrontendMessages = <T extends ErrorSpreadsheetResult>(
  rowItem: T,
): void => {
  const backendMessageList: ErrorMessage[] = [];
  const errorColumnList: string[] = [];
  for (const message of rowItem.messageList) {
    if (message.backendError) {
      backendMessageList.push(message);
      if (message.errorColumn && !errorColumnList.includes(message.errorColumn))
        errorColumnList.push(message.errorColumn);
    }
  }
  rowItem.messageList = backendMessageList;
  rowItem.errorColumnList = errorColumnList;
  rowItem.errorMessage = formatErrorMesage(rowItem.messageList);
};

/**
 * Clears Backend errors from messageList and errorColumnList.
 * @param rowItem One line that was edited
 * @returns fixed version rowItem
 */
export const clearBackenddMessages = <T extends ErrorSpreadsheetResult>(
  rowItem: T,
): T => {
  const nonBackendMessageList: ErrorMessage[] = [];
  const errorColumnList: string[] = [];
  for (const message of rowItem.messageList) {
    if (!message.backendError) {
      nonBackendMessageList.push(message);
      if (message.errorColumn && !errorColumnList.includes(message.errorColumn))
        errorColumnList.push(message.errorColumn);
    }
  }
  return {
    ...rowItem,
    messageList: nonBackendMessageList,
    errorColumnList: errorColumnList,
    errorMessage: formatErrorMesage(nonBackendMessageList),
  };
};

/**
 * Sets new error to messageList object.
 * @param rowItem One row that was edited by user..
 * @param errorColumns List of string which contains columns with errors
 * @param messageId Message ID for the error
 * @param mesage Message for the error
 */
export const setFrontendErrorMessage = <T extends ErrorSpreadsheetResult>(
  rowItem: T,
  errorColumns: string[],
  messageId: string,
  message: string,
): void => {
  errorColumns.forEach((errorColumn) => {
    const error: ErrorMessage = {
      errorColumn: errorColumn,
      messageId: messageId,
      message: message,
      backendError: false,
    };
    rowItem.messageList.push(error);
    rowItem.errorColumnList.push(errorColumn);
  });
  rowItem.errorMessage = formatErrorMesage(rowItem.messageList);
};

/**
 * Format frontend correlation error message to a string.
 * @param messageList List of ErrorMessage from SpreadsheetState.
 * @returns message as string
 */
const formatErrorMesage = (messageList: ErrorMessage[]): string => {
  const errorMessageList = messageList.map((element) => {
    return `[${element.messageId}] ${element.message}`;
  });
  return errorMessageList.join("\n");
};
